import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  GreenhouseActionType,
  GreenhouseAction,
  NextResumeScreeningAction,
  ResumeScreeningConfig,
  ScreenType,
} from '@scale/shared-types';
import ReactFlow, { Node, Edge, Controls } from 'reactflow';
import { ResumeScreenActionNode } from './ResumeScreenActionNode';
import { ResumeScreenRootNode } from './ResumeScreenRootNode';
import { CreateResumeScreeningSteps } from 'react-scale/components/GrowthCenter/constants';
import { Spinner } from '@scale/scaleui';
import { ResumeScreeningActionNodeStatus } from '../constants';

const NODE_SPACING = 225;
const Y_OFFSET = 75;
const Y_CHILD_OFFSET = 300;
const X_OFFSET = 25;

const createRootNode = (
  id: string,
  position: { x: number; y: number },
  label: string,
  activeDetailPanel: CreateResumeScreeningSteps,
  activeJobConfigId: string,
) => {
  const isActive =
    activeDetailPanel === CreateResumeScreeningSteps.ImportGreenhouseJob &&
    id === activeJobConfigId;

  return {
    id,
    type: 'rootNode',
    position,
    data: { label, isActive },
  };
};

const createActionNode = (
  id: string, // Format: <jobConfigId>-<actionType>
  position: { x: number; y: number },
  label: string,
  actionDetails: string[],
  activeDetailPanel: CreateResumeScreeningSteps,
  activeJobConfigId: string,
  activeJobConfigType: ScreenType,
) => {
  const isDisabled = activeJobConfigType === ScreenType.OCP;
  const isActive =
    id.includes(activeJobConfigId) &&
    ((activeDetailPanel === CreateResumeScreeningSteps.PassActions && label === 'Pass') ||
      (activeDetailPanel === CreateResumeScreeningSteps.FailActions && label === 'Fail') ||
      (activeDetailPanel === CreateResumeScreeningSteps.NeedsHumanReviewActions &&
        label === 'Human Review'));
  const status = isDisabled
    ? ResumeScreeningActionNodeStatus.Disabled
    : isActive
    ? ResumeScreeningActionNodeStatus.Active
    : ResumeScreeningActionNodeStatus.Inactive;
  return {
    id,
    type: 'actionNode',
    position,
    data: { label, actionDetails, status },
    actionDetails,
  };
};

const createEdge = (source: string, target: string) => ({
  id: `${source}-${target}`,
  source,
  target,
});

export const ResumeScreenFlow: React.FC<{
  selectedJobConfigId?: string;
  allJobConfigs: ResumeScreeningConfig[];
  activeJobConfig: ResumeScreeningConfig;
  setActiveJobConfig: (jobConfig: ResumeScreeningConfig) => void;
  activeDetailPanel: CreateResumeScreeningSteps;
  setActiveDetailPanel: (panel: CreateResumeScreeningSteps) => void;
  loadingFlowDiagram: boolean;
}> = ({
  selectedJobConfigId,
  allJobConfigs,
  activeJobConfig,
  setActiveJobConfig,
  activeDetailPanel,
  setActiveDetailPanel,
  loadingFlowDiagram,
}) => {
  const selectedJobConfig = allJobConfigs.find(config => config._id === selectedJobConfigId);

  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const nodeTypes = useMemo(
    () => ({ rootNode: ResumeScreenRootNode, actionNode: ResumeScreenActionNode }),
    [],
  );

  const handleNodeClick = (node: Node) => {
    if (node.type === 'rootNode') {
      const activePanel = CreateResumeScreeningSteps.ImportGreenhouseJob;
      const jobConfig = allJobConfigs.find(config => config._id === node.id);
      if (!jobConfig) return;

      setActiveDetailPanel(activePanel);
      setActiveJobConfig(jobConfig);
    } else {
      if (!node.id.includes('-')) return;
      const jobId = node.id.split('-')[0];
      const jobConfig = allJobConfigs.find(config => config._id === jobId);
      if (!jobConfig) return;

      const actionType = node.id.split('-')[1];
      const panel =
        actionType === 'onPassActions'
          ? CreateResumeScreeningSteps.PassActions
          : actionType === 'onFailActions'
          ? CreateResumeScreeningSteps.FailActions
          : CreateResumeScreeningSteps.NeedsHumanReviewActions;

      if (jobConfig.type === ScreenType.OCP) {
        return;
      }

      setActiveDetailPanel(panel);
      setActiveJobConfig(jobConfig);
    }
  };

  const createNodeTree = useCallback(
    (parentId: string, position: { x: number; y: number }, config?: ResumeScreeningConfig) => {
      const newNodes: Node[] = [];
      const newEdges: Edge[] = [];

      const actionTypes: Array<keyof ResumeScreeningConfig> = [
        'onPassActions',
        'onUnsupportedFileActions',
        'onFailActions',
      ];
      actionTypes.forEach((actionType, index) => {
        const actionTypeLabel =
          actionType === 'onPassActions'
            ? 'Pass'
            : actionType === 'onFailActions'
            ? 'Fail'
            : 'Human Review';

        const actions = config ? (config[actionType] as GreenhouseAction[]) : [];
        const actionDetails: string[] = actions?.map(action => {
          if (action.type === GreenhouseActionType.MoveStage) {
            return 'Move Stages';
          } else if (action.type === GreenhouseActionType.EditTags) {
            return 'Edit Tags';
          } else {
            return 'Resume Screening';
          }
        });

        const nodeId = `${parentId}-${actionType}`;
        newNodes.push(
          createActionNode(
            nodeId,
            { x: X_OFFSET + position.x + (index - 1) * NODE_SPACING, y: position.y + Y_OFFSET },
            actionTypeLabel,
            actionDetails,
            activeDetailPanel,
            activeJobConfig._id,
            activeJobConfig.type,
          ),
        );
        newEdges.push(createEdge(parentId, nodeId));

        // Recursive next screen behavior
        const nextResumeScreenAction = actions?.find(
          action => action.type === GreenhouseActionType.NextResumeScreening,
        ) as NextResumeScreeningAction;
        if (nextResumeScreenAction) {
          const nextResumeScreenConfig = allJobConfigs.find(
            jobConfig => jobConfig._id === nextResumeScreenAction.nextResumeScreeningConfigId,
          );
          if (!nextResumeScreenConfig) return;

          const rootPosition = {
            x: position.x + (index - 1) * NODE_SPACING,
            y: position.y + Y_CHILD_OFFSET,
          };
          const childRootNodeId = `${nextResumeScreenConfig._id}`;
          newNodes.push(
            createRootNode(
              childRootNodeId,
              rootPosition,
              nextResumeScreenConfig.name || 'Resume Screening',
              activeDetailPanel,
              activeJobConfig._id,
            ),
          );
          newEdges.push(createEdge(nodeId, childRootNodeId));

          // Recursively create the child node tree
          const childNodesAndEdges = createNodeTree(
            childRootNodeId,
            rootPosition,
            nextResumeScreenConfig,
          );
          newNodes.push(...childNodesAndEdges.nodes);
          newEdges.push(...childNodesAndEdges.edges);
        }
      });

      return { nodes: newNodes, edges: newEdges };
    },
    [allJobConfigs, activeDetailPanel, activeJobConfig],
  );

  useEffect(() => {
    const rootNodeId = selectedJobConfig?._id ?? 'root';
    const rootNode: Node = createRootNode(
      rootNodeId,
      { x: 0, y: 0 },
      selectedJobConfig?.name || 'Resume Screening',
      activeDetailPanel,
      activeJobConfig._id,
    );

    const { nodes: childNodes, edges: childEdges } = createNodeTree(
      rootNode.id,
      { x: 0, y: 0 },
      selectedJobConfig,
    );

    setNodes([rootNode, ...childNodes]);
    setEdges(childEdges);
  }, [selectedJobConfig, createNodeTree, allJobConfigs, activeDetailPanel, activeJobConfig]);

  return (
    <div className="scaleui scale-tailwind flex h-full w-full items-center justify-center">
      {loadingFlowDiagram ? (
        <div className="flex flex-grow justify-center items-center">
          <Spinner size="lg" />
        </div>
      ) : (
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          fitView
          onNodeClick={(_, node) => handleNodeClick(node)}
        >
          <Controls />
        </ReactFlow>
      )}
    </div>
  );
};
